import { Link } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Cpu,
  Shield,
  Star,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import type { ModerationFieldAvailability } from '../dashboardTypes';
import { DashboardBusy, NotReadyBadge, moderationReadyState } from './DashboardNotReady';

/** KPI keys that navigate to related Admin pages. */
const KPI_LINKS: Partial<Record<string, string>> = {
  // Intake volume → full Coop cases queue (all non-terminal statuses).
  CONTENT_MODERATED: (() => {
    const params = new URLSearchParams();
    params.set('view', 'queue');
    return `${ROUTES.moderation}?${params.toString()}`;
  })(),
  REPORTS_RECEIVED: ROUTES.moderationReports,
  // Same deep link as the pipeline funnel Human Review stage.
  HUMAN_REVIEW: (() => {
    const params = new URLSearchParams();
    params.set('view', 'queue');
    params.set('coop-queue.f', JSON.stringify({ status: ['escalated'] }));
    return `${ROUTES.moderation}?${params.toString()}`;
  })(),
};

const KPI_LINK_LABELS: Partial<Record<string, string>> = {
  CONTENT_MODERATED: 'open moderation cases queue',
  REPORTS_RECEIVED: 'open User Reports',
  HUMAN_REVIEW: 'open human review queue',
};

export interface ModerationKpiView {
  key?: string;
  label: string;
  value: number | null;
  delta: number | null;
  description?: string;
  availability?: ModerationFieldAvailability;
  note?: string;
}

const KPI_ICONS: Record<string, { icon: LucideIcon; color: string; upIsGood: boolean }> = {
  CONTENT_MODERATED: { icon: Shield, color: '#60a5fa', upIsGood: false },
  AI_AUTO_REJECTED: { icon: Cpu, color: '#f87171', upIsGood: false },
  SYSTEM_HEALTH: { icon: Activity, color: '#34d399', upIsGood: true },
  REPORTS_RECEIVED: { icon: AlertCircle, color: '#60a5fa', upIsGood: false },
  HUMAN_REVIEW: { icon: Users, color: '#60a5fa', upIsGood: false },
  ACTIVE_PENALTIES: { icon: Star, color: '#f87171', upIsGood: false },
};

function formatCount(value: number | null | undefined, key?: string): string {
  if (value == null) return '—';
  if (key === 'SYSTEM_HEALTH') return `${new Intl.NumberFormat().format(value)}%`;
  return new Intl.NumberFormat().format(value);
}

function kpiMeta(key?: string) {
  return KPI_ICONS[key ?? ''] ?? { icon: Shield, color: '#94a3b8', upIsGood: true };
}

/** Summary Metrics strip for the Moderation Dashboard (TM-3450). */
const TOP_KEYS = new Set(['CONTENT_MODERATED', 'AI_AUTO_REJECTED', 'SYSTEM_HEALTH']);
const SECONDARY_KEYS = new Set(['REPORTS_RECEIVED', 'HUMAN_REVIEW', 'ACTIVE_PENALTIES']);

export function ModerationSummaryKpis({
  metrics,
  comparisonLabel,
  className,
  isLoading,
  /** Figma: `top` = Content/AI/Health; `secondary` = Reports/Human/Active. Default all. */
  group = 'all',
}: {
  metrics: ModerationKpiView[];
  comparisonLabel?: string;
  className?: string;
  isLoading?: boolean;
  group?: 'all' | 'top' | 'secondary';
}) {
  const filtered =
    group === 'top'
      ? metrics.filter((m) => TOP_KEYS.has(m.key ?? ''))
      : group === 'secondary'
        ? metrics.filter((m) => SECONDARY_KEYS.has(m.key ?? ''))
        : metrics;

  if (isLoading) {
    if (group === 'secondary') return null;
    return (
      <div className={cn('grid gap-4 sm:grid-cols-3', className)} aria-busy="true">
        <DashboardBusy className="rounded-xl border border-border/40 bg-[#1a1a1c]" />
      </div>
    );
  }

  if (filtered.length === 0) return null;

  return (
    <div className={cn('grid gap-4 sm:grid-cols-3', className)}>
      {filtered.map((metric) => {
        const state = moderationReadyState(metric.availability);
        const live = state === 'live' || state === 'partial' || state === 'snapshot';
        const { icon: Icon, color } = kpiMeta(metric.key);
        const href = metric.key ? KPI_LINKS[metric.key] : undefined;
        const delta = metric.delta;
        const rising = (delta ?? 0) >= 0;
        const DeltaIcon = rising ? ArrowUp : ArrowDown;

        const card = (
          <Card
            className={cn(
              'border-border/40 bg-[#1a1a1c] shadow-none',
              href && 'transition-colors hover:border-sky-500/40 hover:bg-[#1f1f22]',
            )}
            title={metric.description ?? undefined}
          >
            <CardContent className="space-y-2 p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Icon className="size-4" style={{ color }} aria-hidden />
                  <span className="text-sm font-medium text-muted-foreground">{metric.label}</span>
                </span>
                <NotReadyBadge
                  state={state}
                  {...(metric.note || metric.description
                    ? { hint: metric.note ?? metric.description }
                    : {})}
                />
              </div>
              <p
                className={cn(
                  'text-[32px] font-bold leading-none tracking-tight tabular-nums',
                  !live && 'text-muted-foreground',
                )}
              >
                {live ? formatCount(metric.value, metric.key) : '—'}
              </p>
              {live && delta != null && comparisonLabel ? (
                <p className="flex items-center gap-1 text-xs">
                  <span
                    className={cn(
                      'inline-flex items-center gap-0.5 font-semibold',
                      rising ? 'text-emerald-400' : 'text-rose-400',
                    )}
                  >
                    <DeltaIcon className="size-3" aria-hidden />
                    {Math.abs(delta).toFixed(1)}%
                  </span>
                  <span className="text-muted-foreground">vs {comparisonLabel}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">No prior comparison</p>
              )}
              {metric.note ? (
                <p className="text-[11px] leading-4 text-sky-400/90">{metric.note}</p>
              ) : null}
            </CardContent>
          </Card>
        );

        const linkLabel =
          (metric.key ? KPI_LINK_LABELS[metric.key] : undefined) ?? 'open details';

        return href ? (
          <Link
            key={metric.key ?? metric.label}
            to={href}
            className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
            aria-label={`${metric.label}: ${linkLabel}`}
          >
            {card}
          </Link>
        ) : (
          <div key={metric.key ?? metric.label}>{card}</div>
        );
      })}
    </div>
  );
}
